import React, { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, Divider, Stack, TextField, Typography, Unstable_Grid2 as Grid
} from '@mui/material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import AppBarContext from 'context/AppBarContext';
import { COMPANY_SETTINGS, UPDATE_COMPANY_SETTINGS } from 'api/graphql/companySettings';

const FIELDS = [
  ['name', 'Nombre comercial'],
  ['legal_name', 'Razón social'],
  ['support_url', 'Sitio / soporte'],
  ['co_nit', 'NIT'],
  ['co_tax_regime', 'Régimen (ej. Responsable de IVA)'],
  ['co_address', 'Dirección (Colombia)'],
  ['co_phone', 'Teléfono'],
  ['co_dian_resolution', 'Resolución DIAN'],
  ['us_tax_id', 'Tax ID (internacional)'],
  ['us_address', 'Dirección (internacional)']
];

// Parámetros de suscripción/cancelación mostrados en el checkout (texto largo, por idioma).
const TERMS_FIELDS = [
  ['subscription_terms_es', 'Parámetros de suscripción/cancelación (Español)'],
  ['subscription_terms_en', 'Subscription/cancellation parameters (English)']
];
const ALL_KEYS = [...FIELDS, ...TERMS_FIELDS];

/**
 * Datos del emisor que aparecen en las facturas (NIT, régimen de IVA, dirección, etc.).
 * Editable por admin; sobrescribe la config por defecto.
 */
function BillingSettings() {
  const { t } = useTranslation(['organizations', 'routes', 'buttons']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const pageTitle = t('billingInfo', { ns: 'routes', defaultValue: 'Datos de facturación' });

  const { data, loading } = useQuery(COMPANY_SETTINGS, { fetchPolicy: 'cache-and-network' });
  const [updateSettings, { loading: saving }] = useMutation(UPDATE_COMPANY_SETTINGS);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  useEffect(() => {
    if (data?.companySettings && !form) {
      const s = data.companySettings;
      const next = {};
      ALL_KEYS.forEach(([key]) => { next[key] = s[key] ?? ''; });
      setForm(next);
    }
  }, [data, form]);

  const handleSave = async () => {
    const input = {};
    ALL_KEYS.forEach(([key]) => { input[key] = form[key]?.trim() || null; });
    await updateSettings({ variables: { input } });
    setMsg(t('billingSaved', { defaultValue: 'Datos de facturación guardados.' }));
  };

  if (loading && !form) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} sx={{ color: 'gold.main' }} /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <SEOPageTitle title={pageTitle} />
      <PremiumCard padding={{ xs: 3, md: 4 }}>
        <Typography variant="h6" fontWeight={800}>{t('issuerData', { defaultValue: 'Datos del emisor' })}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t('issuerHelp', { defaultValue: 'Estos datos aparecen en las facturas de todos los pagos.' })}
        </Typography>
        <Divider sx={{ my: 2.5, borderColor: 'hairline.main' }} />

        <Grid container spacing={2}>
          {FIELDS.map(([key, label]) => (
            <Grid xs={12} sm={6} key={key}>
              <TextField
                label={label}
                value={form?.[key] ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                fullWidth
              />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'hairline.main' }} />

        <Typography variant="h6" fontWeight={800}>{t('subscriptionParams', { defaultValue: 'Parámetros de suscripción y cancelación' })}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {t('subscriptionParamsHelp', { defaultValue: 'Se muestran en el checkout junto al consentimiento de suscripción.' })}
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          {TERMS_FIELDS.map(([key, label]) => (
            <TextField
              key={key}
              label={label}
              value={form?.[key] ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
              multiline
              minRows={4}
              fullWidth
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }}>
          <Button onClick={handleSave} disabled={saving} sx={{ ...premiumButtonSx, px: 3 }}>
            {t('save', { ns: 'buttons', defaultValue: 'Guardar' })}
          </Button>
          {msg && <Typography variant="caption" sx={{ color: 'var(--otx-gold-text)' }}>{msg}</Typography>}
        </Stack>
      </PremiumCard>
    </Box>
  );
}

export default BillingSettings;
